import apiService from "@/lib/api";

const TENANT_SETTINGS_BASE = "/api/v2/tenant-settings";
const PLATFORM_BASE = "/api/v2/platform";
const ADMIN_BASE = "/api/v2/admin";

/**
 * Guardian (prompt-safety) configuration across its three levels.
 *
 * Resolution, server-side:
 *
 *     platform policy → tenant policy → the tenant's own setting
 *
 * A switch whose resolved policy is `FORCED_ON` / `FORCED_OFF` comes back with
 * `locked: true` plus `lockedBy` and `reason`. Render the reason — a greyed
 * control with no explanation is worse than no control, because the admin
 * cannot tell whether it is broken or deliberate.
 *
 * `unavailableOnDeployment` is a third state, distinct from `value: false`:
 * with `LLM_GUARDIAN_MODEL` unset the guardian cannot run at all, so the UI must
 * say "unavailable on this deployment" rather than show an enabled-looking
 * toggle that silently does nothing.
 */
export const GuardianService = {
  /** The signed-in tenant's `master` switch, resolved and annotated. */
  getSettings: async () => {
    try {
      const response = await apiService.getSecure(`${TENANT_SETTINGS_BASE}/guardian`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while fetching guardian settings:", error);
      throw error;
    }
  },

  /**
   * Flip the tenant's own switches. Partial.
   *
   * A locked switch is rejected with 403 `GUARDIAN_SWITCH_LOCKED` rather than
   * silently ignored — a save that appears to work and then shows the old value
   * is the worst of the three possible behaviours.
   *
   * @param {Object} switches e.g. `{ master: false }`. `master` is the only
   *   key the schema accepts — the per-flow switches were removed, and the
   *   request model is `extra="forbid"`, so an old key is rejected outright.
   */
  updateSettings: async (switches) => {
    try {
      const response = await apiService.patch(`${TENANT_SETTINGS_BASE}/guardian`, switches);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while updating guardian settings:", error);
      throw error;
    }
  },

  /** SuperAdmin: the default policy for every tenant with no override. */
  getPlatformPolicy: async () => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/guardian-policy`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while fetching platform guardian policy:", error);
      throw error;
    }
  },

  /**
   * SuperAdmin: set the fleet-wide policy.
   * @param {Object} policies e.g. `{ master: "FORCED_ON" }`
   */
  updatePlatformPolicy: async (policies) => {
    try {
      const response = await apiService.putSecure(`${PLATFORM_BASE}/guardian-policy`, { policies });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while updating platform guardian policy:", error);
      throw error;
    }
  },

  /** SuperAdmin: one tenant's overrides, plus the platform defaults they override. */
  getTenantPolicy: async (tenantId) => {
    try {
      const response = await apiService.getSecure(`${ADMIN_BASE}/tenants/${tenantId}/guardian-policy`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while fetching tenant guardian policy:", error);
      throw error;
    }
  },

  /**
   * SuperAdmin: force a switch on/off for one tenant, or hand it back to them.
   *
   * Setting a switch to `TENANT_CHOICE` removes the override so the tenant
   * inherits the platform policy again — rather than pinning them to today's
   * platform default, which would make a later fleet-wide change skip them.
   */
  updateTenantPolicy: async (tenantId, policies) => {
    try {
      const response = await apiService.putSecure(`${ADMIN_BASE}/tenants/${tenantId}/guardian-policy`, { policies });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Error while updating tenant guardian policy:", error);
      throw error;
    }
  },
};

export default GuardianService;
